import { Product, Variant } from '@shared/types/product';
import { Attribute, Product as EntityProduct } from '@/types/entity/product';
import { Currency } from '@/types/currency';
import { Cart } from '@shared/types/cart';
import { Locale } from '@/project.config';
import { mapMasterAttributes } from './map-master-attributes';
import { mapCategory } from './map-category';
import { mapAttribute } from './map-attribute';
import { useParams } from 'next/navigation';
import { localeMapping } from '@/constants/localeMapping';

export const mapProduct = (
  product: Product,
  {
    variantIndex = -1,
    cart,
    // locale = 'au',
    filterOutNonMatchingVariants = true,
  }: { variantIndex?: number; cart?: Cart; locale?: Locale; filterOutNonMatchingVariants?: boolean } = {},
): EntityProduct => {
  const { locale } = useParams();
  const localeString = Array.isArray(locale) ? locale[0] : locale || '';
  const localeKey = localeMapping[localeString];
  const variant =
    product.variants.find(
      (v) =>
        !!v.discountedPrice?.centAmount && !!v.price?.centAmount && v.discountedPrice.centAmount < v.price?.centAmount,
    ) ?? product.variants[0];

  const cheapeastVariant = product.variants.reduce(
    (a, b) => ((a.price?.centAmount ?? Number.MAX_VALUE) < (b.price?.centAmount ?? Number.MAX_VALUE) ? a : b),
    {
      price: { centAmount: Number.MAX_VALUE },
    } as Variant,
  );

  const mostExpensiveVariant = product.variants.reduce(
    (a, b) => ((a.price?.centAmount ?? Number.MIN_VALUE) > (b.price?.centAmount ?? Number.MIN_VALUE) ? a : b),
    {
      price: { centAmount: Number.MIN_VALUE },
    } as Variant,
  );

  const itemCode = variant?.attributes?.['item-code'];
  const categories = (product?.categories && (product?.categories as any)[localeKey]?.lvl1) || [];
  const categoriesLvl0 = (product?.categories && (product?.categories as any)[localeKey]?.lvl0) || [];
  const [mainCategory, subCategory] = categories.length ? categories[0].split(' > ') : [null, null];
  const itemName = variant?.attributes?.['item-name']?.[locale] ?? product?.name ?? '';
  const tradePriceKey = variant?.attributes?.[`trade-price-${localeString.toUpperCase()}`];
  const exclusiveProduct: any = {
    'en-AU': variant?.attributes?.['exclusive-product-AU'],
    'en-NZ': variant?.attributes?.['exclusive-product-NZ'],
  };
  const runDown = variant?.attributes?.['item-attributes']?.find((attr: { name: string }) => attr.name === 'lifecycle')
    ?.value[localeKey]?.toLowerCase() === 'run down';
  const lifecycle = variant?.attributes?.['item-attributes']?.find((attr: { name: string }) => attr.name === 'lifecycle')
    ?.value[localeKey];
  const isNew = variant?.attributes?.['IsNew'];
  const productType = variant?.attributes?.["product-types"] === 'Brochures';
  const isBuilderRange = variant?.attributes?.['builder-range']?.[localeKey]?.toLowerCase() === 'yes';
  
  return {
    id: product.productId ?? '',
    sku: variant.sku,
    name: itemName,
    description: variant.attributes?.['Product-Specifications'],
    image: variant?.images?.[0] ? [variant.images[0]] : undefined,
    images: variant.images,
    // price: price,
    price: (variant.price?.centAmount ?? 0) / Math.pow(10, variant.price?.fractionDigits ?? 2),
    // discountedPrice: (variant.discountedPrice?.centAmount ?? 0) / Math.pow(10, variant.price?.fractionDigits ?? 2),
    currency: (variant.price?.currencyCode ?? 'AUD') as Currency,
    inStock: variant.isOnStock,
    maxQuantity: variant.isOnStock ? variant.availableQuantity : 0,
    models: itemCode,
    ...(cheapeastVariant &&
      mostExpensiveVariant &&
      cheapeastVariant.price?.centAmount !== mostExpensiveVariant.price?.centAmount
      ? {
        priceRange: [
          (cheapeastVariant.price?.centAmount ?? 0) / Math.pow(10, cheapeastVariant.price?.fractionDigits ?? 2),
          (mostExpensiveVariant.price?.centAmount ?? 0) /
          Math.pow(10, mostExpensiveVariant.price?.fractionDigits ?? 2),
        ],
      }
      : {}),
    // tradePrice: tradePriceKey,
    productType,
    category: subCategory ?? mainCategory,
    url: product._url,
    exclusiveProduct: Array.isArray(exclusiveProduct[localeKey]) && exclusiveProduct[localeKey].length > 0 && exclusiveProduct[localeKey][0]?.toLowerCase() !== 'null',
    isNew: isNew,
    isBuilderRange,
    runDown,
    lifecycle
  };
};
